import React, { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { History } from 'history';
import { PageLoadingIndicator } from 'client/shared/Spinner';
import {
  COLOR_MUTED_TEXT,
  MOBILE_BREAKPOINT,
  COLOR_MUTED_BACKGROUND,
} from 'components/PaywallsShared/constants';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Metadata } from 'types/Metadata';

const SUBSCRIPTION_DISABLED_TITLE = 'This subscription no longer exists';
const ERROR_ROUTE = '/404';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 400px;
  padding: 70px;
  text-align: center;
  background: ${COLOR_MUTED_BACKGROUND};
`;

const Header = styled.h1`
  display: block;
  margin: 0 0 20px 0;
  font-weight: 800;
  font-size: 2.4rem;
`;

const Content = styled.div`
  max-width: ${MOBILE_BREAKPOINT}px;
  color: ${COLOR_MUTED_TEXT};
  font-size: 1.6rem;
  line-height: 1.5;
`;

interface PaywallsSubscriptionDisabledProps {
  history: History;
  match: {
    params: {
      vanitySlug: string;
    };
  };
}

export const PaywallsSubscriptionDisabled = ({
  history,
  match: {
    params: { vanitySlug },
  },
}: PaywallsSubscriptionDisabledProps) => {
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const forwardTo404 = useCallback(() => history.push(ERROR_ROUTE), [history]);

  useEffect(() => {
    const getData = async () => {
      try {
        const stationId = await AnchorAPI.getStationIdForVanityName(vanitySlug);
        const metaResponse = await AnchorAPI.getPodcastMetadata(stationId);
        // I think we can eventually remove this check and the metadata call, but this keeps the URL feature-flagged for now
        if (!metaResponse?.stationPaywall?.isSubscriptionEnabled) {
          return forwardTo404();
        }
        setMetadata(metaResponse);
      } catch (err) {
        forwardTo404();
      }
      return null;
    };
    if (vanitySlug) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vanitySlug]);

  if (!vanitySlug) {
    forwardTo404();
    return null;
  }

  if (!metadata) {
    return <PageLoadingIndicator color={COLOR_MUTED_BACKGROUND} />;
  }

  return (
    <Container>
      <Header>{SUBSCRIPTION_DISABLED_TITLE}</Header>
      <Content>
        {`The subscription for "${metadata.podcastName}" is no longer available.`}
      </Content>
    </Container>
  );
};
